import React, { useCallback } from "react";
import { w2s, w2pt, w2p, izoom, mm2cm, mm2m } from "../../utils/coords";
import { mutResizeWall, mutSetRoom, mutAddOpening, getOffsetPoints } from "../../engine/mutations";
import { mutAddAnnotation, mutAddGridLine }         from "../../engine/layerMutations";
import CanvasGrid      from "../canvas/CanvasGrid";
import FaceLayer       from "../canvas/FaceLayer";
import WallLayer       from "../canvas/WallLayer";
import OpeningLayer    from "../canvas/OpeningLayer";
import DimensionLabels from "../canvas/DimensionLabels";
import NodeLayer       from "../canvas/NodeLayer";
import DrawPreview     from "../canvas/DrawPreview";
import SnapDot         from "../canvas/SnapDot";
import SymbolLayer     from "../canvas/SymbolLayer";
import AnnotationLayer from "../canvas/AnnotationLayer";
import ConstraintLayer from "../canvas/ConstraintLayer";
import PrimitivesLayer from "../canvas/PrimitivesLayer";
import DimEditor       from "../modals/DimEditor";
import RoomPopup       from "../modals/RoomPopup";
import OpeningPopup    from "../modals/OpeningPopup";
import CADToolbar      from "./CADToolbar";
import TransformPanel  from "./TransformPanel";

const MONO = { fontFamily: "'JetBrains Mono','Fira Code',monospace" };

/** Tiny technical badge for mode hints */
function HintBadge({ children, color = "#3b82f6" }) {
  return (
    <div style={{
      position: "absolute", left: "50%", top: 46, transform: "translateX(-50%)",
      background: "#030508", border: `1px solid ${color}40`,
      borderRadius: 2, padding: "2px 10px",
      fontSize: 9, color, letterSpacing: 0.5,
      fontWeight: 500, ...MONO, animation: "fadeIn .15s",
      pointerEvents: "none", whiteSpace: "nowrap",
    }}>
      {children}
    </div>
  );
}

export default function CADCanvas({
  st, snap, svgSize, pan, zoom,
  mode, matId, setMatId, wallH, setWallH,
  wStart, sel, setSel,
  selAnnotation, setSelAnnotation,
  dragNode, setDragNode, dragPosMm, setDragPosMm,
  dragNodes, setDragNodes, dragAnchor, setDragAnchor, dragDelta, setDragDelta,
  beginDrag, commitDrag,
  showHE, showLayerPanel, activeSymbol, setActiveSymbol,
  selMany, setSelMany, selBox,
  dragSymbol, setDragSymbol, dragSymPos, setDragSymPos,
  dragAnn, setDragAnn, dragAnnPos, setDragAnnPos,
  offsetDist, setOffsetDist, offsetHoverEdge, offsetSide,
  editingDim, setEditingDim,
  roomPopup, setRoomPopup,
  openingPopup, setOpeningPopup,
  pendingAnnotation, setPendingAnnotation,
  pendingGridLine, setPendingGridLine,
  annotationDraft, setAnnotationDraft,
  unitPreset,
  hasErr, errCount,
  commit, clearSel,
  svgRef, onMM, onMD, onMU, onWh, onSvgClick, onSvgDbl,
  onSetMode, onTemplates, onDrawing, onHeal, onToggleLayers,
  showLayerPanelState,
  // Transform panel
  showTransformPanel, onToggleTransform,
  transformAngle, setTransformAngle,
  transformScale, setTransformScale,
  arrayRows, setArrayRows,
  arrayCols, setArrayCols,
  arrayRowSpacing, setArrayRowSpacing,
  arrayColSpacing, setArrayColSpacing,
  arrayCount, setArrayCount,
  arrayTotalAngle, setArrayTotalAngle,
  onCommand,
  mirrorAxisStart,
  adimPts,
  trimTarget,
  show3D, onToggle3D,
  showAI, onToggleAI,
  primDrawPts,
  selPrim,
  drawingMode,
  onSetDrawingMode,
}) {
  const cursorScr = snap ? w2s(snap.point, pan, zoom) : null;

  // ── Cursor style ──────────────────────────────────────────────────────────
  const cursor = (() => {
    if (dragNodes) return "grabbing";
    if (mode === "wall" || mode === "dim" || mode === "adim") return wStart || adimPts?.length ? "crosshair" : "cell";
    if (mode === "trim") return "crosshair";
    if (mode === "circle") return primDrawPts?.length ? "crosshair" : "cell";
    if (mode === "arc" || mode === "spline" || mode === "polyline") return "crosshair";
    if (mode === "square" || mode === "rect" || mode === "squarewall" || mode === "rectwall") return primDrawPts?.length ? "crosshair" : "cell";
    if (mode === "opening" || mode === "annotate") return "crosshair";
    return "default";
  })();

  return (
    <div style={{ flex: 1, position: "relative", paddingTop: 38, overflow: "hidden" }}>

      {/* ── SVG — infinite canvas via pan transform ───────────────────────── */}
      <svg
        ref={svgRef}
        style={{ width: "100%", height: "100%", userSelect: "none", cursor }}
        onMouseMove={onMM} onMouseDown={onMD} onMouseUp={onMU}
        onClick={onSvgClick} onDoubleClick={onSvgDbl} onWheel={onWh}
      >
        {/* Grid fills the visible viewport, not the world */}
        <CanvasGrid w={svgSize.w} h={svgSize.h} pan={pan} zoom={zoom}/>

        {/* World-space transform — all drawing content */}
        <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>

          {/* ── World origin cross ──────────────────────────────────────── */}
          <g opacity={0.25} style={{ pointerEvents: "none" }}>
            <line x1={-20} y1={0} x2={20} y2={0} stroke="#3b82f6" strokeWidth={1/zoom}/>
            <line x1={0} y1={-20} x2={0} y2={20} stroke="#3b82f6" strokeWidth={1/zoom}/>
            <circle cx={0} cy={0} r={3/zoom} fill="#3b82f6"/>
          </g>

          <FaceLayer st={st} selFaceId={sel.face} zoom={zoom}
            selManyFaces={selMany?.faces || []}
            onSelect={id => setSel(p => ({ ...p, face: id, edge: null, opening: null }))}/>

          <WallLayer st={st} selEdgeId={sel.edge} showHE={showHE} zoom={zoom}
            selManyEdges={selMany?.edges || []}
            dragNodeId={dragNode} dragPosMm={dragPosMm}
            dragNodes={dragNodes} dragDelta={dragDelta}
            trimTarget={trimTarget}
            onSelect={id => setSel(p => ({ ...p, edge: id, face: null, node: null, opening: null }))}/>

          <OpeningLayer st={st} selOpeningId={sel.opening} zoom={zoom}
            onSelect={id => setSel(p => ({ ...p, opening: id, edge: null, face: null, node: null }))}/>

          <DimensionLabels st={st} zoom={zoom} unitPreset={unitPreset}
            onEditDim={(eId, lenMm, svgPt) => setEditingDim({ eId, lenMm, svgPt })}/>

          <NodeLayer st={st} selNodeId={sel.node} zoom={zoom}
            mode={mode}
            selManyNodes={selMany?.nodes || []}
            dragNodeId={dragNode} dragPosMm={dragPosMm}
            dragNodes={dragNodes} dragDelta={dragDelta}
            onSelect={id => setSel(p => ({ ...p, node: id, edge: null, face: null, opening: null }))}
            onDragStart={id => {
              if (mode !== "select") return;
              const anchorPt = snap?.point || st.nodes[id];

              // If dragged node is in multi-select — drag all selected nodes together
              if (selMany?.nodes?.includes(id) && selMany.nodes.length > 1) {
                const startPositions = selMany.nodes.map(nId => {
                  const n = st.nodes[nId];
                  return { id: nId, x0: n?.x ?? 0, y0: n?.y ?? 0 };
                });
                setDragNodes(startPositions);
                setDragAnchor(anchorPt);
                setDragDelta({ dx: 0, dy: 0 });
                beginDrag();
              } else {
                // Single-node drag
                setSel(p => ({ ...p, node: id, edge: null, face: null, opening: null }));
                setDragNode(id);
                setDragPosMm(st.nodes[id]);
                beginDrag();
              }
            }}/>

          {/* Wall / line draw preview */}
          {(mode === "wall") && wStart && snap && (
            <DrawPreview startMm={wStart} cursorMm={snap.point} zoom={zoom} unitPreset={unitPreset}/>
          )}

          {/* Chain start indicator */}
          {mode === "wall" && wStart && (() => {
            const p  = w2pt(wStart);
            const s2 = izoom(zoom);
            return (
              <g style={{ pointerEvents: "none" }}>
                <circle cx={p.x} cy={p.y} r={s2 * 5}
                  fill="none" stroke="#3b82f6"
                  strokeWidth={s2} strokeDasharray={`${s2*3},${s2*2}`} opacity={0.5}/>
                <circle cx={p.x} cy={p.y} r={s2 * 2} fill="#3b82f6"/>
              </g>
            );
          })()}

          {/* Dim mode — first point indicator */}
          {mode === "dim" && wStart && (() => {
            const p  = w2pt(wStart);
            const s2 = izoom(zoom);
            return (
              <g style={{ pointerEvents: "none" }}>
                <circle cx={p.x} cy={p.y} r={s2*4} fill="none" stroke="#818cf8" strokeWidth={s2}/>
                <circle cx={p.x} cy={p.y} r={s2*2} fill="#818cf8"/>
              </g>
            );
          })()}

          {/* Angular dim — show collected click points */}
          {mode === "adim" && adimPts?.length > 0 && (() => {
            const s2 = izoom(zoom);
            const labels = ["V", "A", "B"];
            return (
              <g style={{ pointerEvents: "none" }}>
                {adimPts.map((pt, i) => {
                  const p = w2pt(pt);
                  return (
                    <g key={i}>
                      <circle cx={p.x} cy={p.y} r={s2 * 4} fill="#818cf8" opacity={0.9}/>
                      <text x={p.x + s2*6} y={p.y - s2*4} fontSize={s2*8}
                        fill="#818cf8" fontFamily="'JetBrains Mono',monospace">{labels[i]}</text>
                      {i > 0 && (() => {
                        const prev = w2pt(adimPts[i-1]);
                        return <line x1={prev.x} y1={prev.y} x2={p.x} y2={p.y}
                          stroke="#818cf8" strokeWidth={s2*0.7} strokeDasharray={`${s2*4},${s2*2}`} opacity={0.5}/>;
                      })()}
                    </g>
                  );
                })}
              </g>
            );
          })()}

          {/* Mirror mode — axis preview */}
          {mode === "mirror" && (() => {
            const s2 = izoom(zoom);
            const curPt = snap?.point;
            return (
              <g style={{ pointerEvents: "none" }}>
                {mirrorAxisStart && curPt && (() => {
                  const a = w2pt(mirrorAxisStart), b = w2pt(curPt);
                  // Extend line beyond endpoints for clarity
                  const dx = b.x - a.x, dy = b.y - a.y;
                  const L  = Math.sqrt(dx*dx + dy*dy) || 1;
                  const ex = dx/L * 2000, ey = dy/L * 2000;
                  return <line
                    x1={a.x - ex} y1={a.y - ey} x2={b.x + ex} y2={b.y + ey}
                    stroke="#f472b6" strokeWidth={s2 * 1.5}
                    strokeDasharray={`${s2*6},${s2*3}`} opacity={0.8}
                  />;
                })()}
                {mirrorAxisStart && (() => {
                  const p = w2pt(mirrorAxisStart);
                  return <circle cx={p.x} cy={p.y} r={s2*4} fill="#f472b6" opacity={0.8}/>;
                })()}
                {curPt && (() => {
                  const p = w2pt(curPt);
                  return <circle cx={p.x} cy={p.y} r={s2*3} fill="none"
                    stroke="#f472b6" strokeWidth={s2} opacity={0.6}/>;
                })()}
              </g>
            );
          })()}

          <SnapDot snap={snap} zoom={zoom}/>

          {/* ── Offset preview overlay ─────────────────────────────── */}
          {mode === "offset" && offsetHoverEdge && (() => {
            const s = 1 / (0.2 * zoom);
            const snapState = snap;
            // Determine side from cursor position
            const pts = getOffsetPoints(offsetHoverEdge, 1, "left", st);
            if (!pts) return null;
            const dx = pts.nB.x - pts.nA.x, dy = pts.nB.y - pts.nA.y;
            const L  = Math.sqrt(dx*dx + dy*dy) || 1;
            const nx = -dy/L, ny = dx/L;
            const toCursor = snapState?.point
              ? { x: snapState.point.x - pts.nA.x, y: snapState.point.y - pts.nA.y }
              : { x: 0, y: 1 };
            const side = (toCursor.x * nx + toCursor.y * ny) >= 0 ? "left" : "right";
            const oPts = getOffsetPoints(offsetHoverEdge, offsetDist, side, st);
            if (!oPts) return null;
            // Convert world-mm to pixel space (WORLD_SCALE = 0.2)
            const WS = 0.2;
            const ax = oPts.ptA.x * WS, ay = oPts.ptA.y * WS;
            const bx = oPts.ptB.x * WS, by = oPts.ptB.y * WS;
            // Source line in pixel space
            const sax = pts.nA.x * WS, say = pts.nA.y * WS;
            const sbx = pts.nB.x * WS, sby = pts.nB.y * WS;
            return (
              <g style={{ pointerEvents:"none" }}>
                {/* Source highlight */}
                <line x1={sax} y1={say} x2={sbx} y2={sby}
                  stroke="#22d3ee" strokeWidth={s * 0.5} opacity={0.4}/>
                {/* Preview offset line */}
                <line x1={ax} y1={ay} x2={bx} y2={by}
                  stroke="#22d3ee" strokeWidth={s * 0.8}
                  strokeDasharray={`${s*5},${s*3}`} opacity={0.9}/>
                {/* Endpoint dots */}
                <circle cx={ax} cy={ay} r={s*2.5} fill="#22d3ee" opacity={0.8}/>
                <circle cx={bx} cy={by} r={s*2.5} fill="#22d3ee" opacity={0.8}/>
                {/* Distance arrow + label */}
                {(() => {
                  const mx = (ax+bx)/2, my = (ay+by)/2;
                  const smx = (sax+sbx)/2, smy = (say+sby)/2;
                  return (
                    <>
                      <line x1={smx} y1={smy} x2={mx} y2={my}
                        stroke="#22d3ee" strokeWidth={s*0.4}
                        strokeDasharray={`${s*2},${s*2}`} opacity={0.5}/>
                      <text x={mx + s*5} y={my - s*5}
                        fontSize={s*7} fill="#22d3ee"
                        fontFamily="'JetBrains Mono',monospace" fontWeight="700">
                        {offsetDist >= 1000 ? (offsetDist/1000).toFixed(2)+"m" : offsetDist+"mm"}
                      </text>
                    </>
                  );
                })()}
              </g>
            );
          })()}

          {/* Geometric primitives — circles, arcs, splines, polylines */}
          <PrimitivesLayer st={st} selId={sel.primitive} zoom={zoom}
            onSelect={(type, id) => setSel(p => ({ ...p, primitive: { type, id }, edge: null, face: null, node: null, opening: null }))}/>

          {/* ── Circle draw ghost ─────────────────────────────────────────── */}
          {mode === "circle" && primDrawPts?.length === 1 && snap && (() => {
            const s2 = izoom(zoom);
            const center = w2pt(primDrawPts[0]);
            const cur    = w2pt(snap.point);
            const r      = Math.sqrt((cur.x-center.x)**2 + (cur.y-center.y)**2);
            return (
              <g style={{ pointerEvents:"none" }}>
                <circle cx={center.x} cy={center.y} r={s2*4} fill="#22d3ee" opacity={0.8}/>
                <circle cx={center.x} cy={center.y} r={r}
                  fill="none" stroke="#22d3ee" strokeWidth={s2*0.8}
                  strokeDasharray={`${s2*5},${s2*3}`} opacity={0.7}/>
                <line x1={center.x} y1={center.y} x2={cur.x} y2={cur.y}
                  stroke="#22d3ee" strokeWidth={s2*0.5} strokeDasharray={`${s2*3},${s2*2}`} opacity={0.5}/>
              </g>
            );
          })()}

          {/* ── Arc draw ghost ────────────────────────────────────────────── */}
          {mode === "arc" && primDrawPts?.length > 0 && snap && (() => {
            const s2  = izoom(zoom);
            const pts = [...primDrawPts, snap.point];
            return (
              <g style={{ pointerEvents:"none" }}>
                {pts.map((pt, i) => {
                  const p = w2pt(pt);
                  return <circle key={i} cx={p.x} cy={p.y} r={s2*4} fill="#22d3ee" opacity={0.9}/>;
                })}
                {pts.length >= 2 && (() => {
                  const [p1, p2] = pts;
                  const sp1 = w2pt(p1), sp2 = w2pt(p2);
                  return <line x1={sp1.x} y1={sp1.y} x2={sp2.x} y2={sp2.y}
                    stroke="#22d3ee" strokeWidth={s2*0.6} strokeDasharray={`${s2*4},${s2*2}`} opacity={0.5}/>;
                })()}
              </g>
            );
          })()}

          {/* ── Square draw ghost ────────────────────────────────────────── */}
          {mode === "square" && primDrawPts?.length === 1 && snap && (() => {
            const s2 = izoom(zoom);
            const center = w2pt(primDrawPts[0]);
            const cur = w2pt(snap.point);
            const dx = cur.x - center.x, dy = cur.y - center.y;
            const diag = Math.sqrt(dx*dx + dy*dy);
            const side = diag / Math.sqrt(2);
            const hx = side / 2;
            const corners = [
              { x: center.x - hx, y: center.y - hx },
              { x: center.x + hx, y: center.y - hx },
              { x: center.x + hx, y: center.y + hx },
              { x: center.x - hx, y: center.y + hx },
            ];
            return (
              <g style={{ pointerEvents:"none" }}>
                <rect x={corners[0].x} y={corners[0].y} width={side} height={side}
                  fill="none" stroke="#22d3ee" strokeWidth={s2*0.8}
                  strokeDasharray={`${s2*5},${s2*3}`} opacity={0.7}/>
                <circle cx={center.x} cy={center.y} r={s2*3} fill="#22d3ee" opacity={0.6}/>
                {corners.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={s2*2} fill="#22d3ee80" opacity={0.7}/>
                ))}
              </g>
            );
          })()}

          {/* ── Rectangle draw ghost ───────────────────────────────────────── */}
          {mode === "rect" && primDrawPts?.length === 1 && snap && (() => {
            const s2 = izoom(zoom);
            const p0 = w2pt(primDrawPts[0]);
            const p1 = w2pt(snap.point);
            const x0 = Math.min(p0.x, p1.x), x1 = Math.max(p0.x, p1.x);
            const y0 = Math.min(p0.y, p1.y), y1 = Math.max(p0.y, p1.y);
            const w = x1 - x0, h = y1 - y0;
            return (
              <g style={{ pointerEvents:"none" }}>
                <rect x={x0} y={y0} width={w} height={h}
                  fill="none" stroke="#22d3ee" strokeWidth={s2*0.8}
                  strokeDasharray={`${s2*5},${s2*3}`} opacity={0.7}/>
                <circle cx={p0.x} cy={p0.y} r={s2*3} fill="#22d3ee" opacity={0.8}/>
                <circle cx={p1.x} cy={p1.y} r={s2*3} fill="#22d3ee" opacity={0.8}/>
              </g>
            );
          })()}

          {/* ── Square Wall draw ghost (yellow) ────────────────────────────── */}
          {mode === "squarewall" && primDrawPts?.length === 1 && snap && (() => {
            const s2 = izoom(zoom);
            const center = w2pt(primDrawPts[0]);
            const cur = w2pt(snap.point);
            const dx = cur.x - center.x, dy = cur.y - center.y;
            const diag = Math.sqrt(dx*dx + dy*dy);
            const side = diag / Math.sqrt(2);
            const hx = side / 2;
            const corners = [
              { x: center.x - hx, y: center.y - hx },
              { x: center.x + hx, y: center.y - hx },
              { x: center.x + hx, y: center.y + hx },
              { x: center.x - hx, y: center.y + hx },
            ];
            return (
              <g style={{ pointerEvents:"none" }}>
                <rect x={corners[0].x} y={corners[0].y} width={side} height={side}
                  fill="none" stroke="#fbbf24" strokeWidth={s2*0.8}
                  strokeDasharray={`${s2*5},${s2*3}`} opacity={0.7}/>
                <circle cx={center.x} cy={center.y} r={s2*3} fill="#fbbf24" opacity={0.6}/>
                {corners.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={s2*2} fill="#fbbf2480" opacity={0.7}/>
                ))}
              </g>
            );
          })()}

          {/* ── Rectangle Wall draw ghost (yellow) ──────────────────────────── */}
          {mode === "rectwall" && primDrawPts?.length === 1 && snap && (() => {
            const s2 = izoom(zoom);
            const p0 = w2pt(primDrawPts[0]);
            const p1 = w2pt(snap.point);
            const x0 = Math.min(p0.x, p1.x), x1 = Math.max(p0.x, p1.x);
            const y0 = Math.min(p0.y, p1.y), y1 = Math.max(p0.y, p1.y);
            const w = x1 - x0, h = y1 - y0;
            return (
              <g style={{ pointerEvents:"none" }}>
                <rect x={x0} y={y0} width={w} height={h}
                  fill="none" stroke="#fbbf24" strokeWidth={s2*0.8}
                  strokeDasharray={`${s2*5},${s2*3}`} opacity={0.7}/>
                <circle cx={p0.x} cy={p0.y} r={s2*3} fill="#fbbf24" opacity={0.8}/>
                <circle cx={p1.x} cy={p1.y} r={s2*3} fill="#fbbf24" opacity={0.8}/>
              </g>
            );
          })()}

          {/* ── Spline / polyline draw ghost ──────────────────────────────── */}
          {(mode === "spline" || mode === "polyline") && primDrawPts?.length > 0 && snap && (() => {
            const s2  = izoom(zoom);
            const all = [...primDrawPts, snap.point].map(p => w2pt(p));
            const pts = all.map(p => `${p.x},${p.y}`).join(" ");
            return (
              <g style={{ pointerEvents:"none" }}>
                <polyline points={pts} fill="none"
                  stroke="#a78bfa" strokeWidth={s2*0.8}
                  strokeDasharray={`${s2*5},${s2*3}`} opacity={0.7}/>
                {all.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={s2*3}
                    fill={i === 0 ? "#a78bfa" : "#a78bfa80"}/>
                ))}
              </g>
            );
          })()}

          {/* ── Current segment measurement for polyline/spline ──────────── */}
          {(mode === "spline" || mode === "polyline") && primDrawPts?.length > 0 && snap && (
            <DrawPreview startMm={primDrawPts[primDrawPts.length - 1]} cursorMm={snap.point} zoom={zoom} unitPreset={unitPreset} showAngle={true}/>
          )}

          <AnnotationLayer st={st} selId={selAnnotation} zoom={zoom}
            onSelect={id => { setSelAnnotation(id); clearSel(); }}
            onDragStart={id => {
              const annId = id.startsWith("ann_") ? id.slice(4) : id;
              const ann = st.annotations?.[annId];
              if (ann) {
                setDragAnn(id);
                setDragAnnPos({ x: ann.x, y: ann.y });
              }
            }}
            dragAnnId={dragAnn}
            dragAnnPos={dragAnnPos}/>

          {/* Constraint indicators — padlocks, ⊥, ∥, H/V badges */}
          <ConstraintLayer st={st} selEdgeId={sel.edge} zoom={zoom}/>

          <SymbolLayer st={st} selSymbolId={sel.symbol} zoom={zoom} mode={mode}
            onSelect={id => setSel(p => ({ ...p, symbol: id, edge: null, face: null, node: null, opening: null }))}
            onDragStart={id => {
              setSel(p => ({ ...p, symbol: id, edge: null, face: null, node: null, opening: null }));
              setDragSymbol(id);
              const sym = st.symbols?.[id];
              if (sym) setDragSymPos({ x: sym.x, y: sym.y });
            }}
            dragSymbolId={dragSymbol} dragPosMm={dragSymPos}/>

          {/* Rulers — thin lines at world x=0 and y=0 extend to infinity */}
          <line x1={-99999} y1={0} x2={99999} y2={0}
            stroke="#0d1520" strokeWidth={1/zoom} style={{ pointerEvents:"none" }}/>
          <line x1={0} y1={-99999} x2={0} y2={99999}
            stroke="#0d1520" strokeWidth={1/zoom} style={{ pointerEvents:"none" }}/>
        </g>

        {/* ── Rubber-band selection box ──────────────────────────────────── */}
        {selBox && (() => {
          const WS = 0.2;
          const ax = selBox.x0 * WS * zoom + pan.x;
          const ay = selBox.y0 * WS * zoom + pan.y;
          const bx = selBox.x1 * WS * zoom + pan.x;
          const by = selBox.y1 * WS * zoom + pan.y;
          return (
            <rect
              x={Math.min(ax,bx)} y={Math.min(ay,by)}
              width={Math.abs(bx-ax)} height={Math.abs(by-ay)}
              fill="#3b82f610" stroke="#3b82f6" strokeWidth={0.8}
              strokeDasharray="4 2" style={{ pointerEvents:"none" }}/>
          );
        })()}

        {/* ── Multi-select edge highlights ─────────────────────────────────── */}
        {selMany && selMany.edges.length > 0 && (
          <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}
            style={{ pointerEvents:"none" }}>
            {selMany.edges.map(eId => {
              const e  = st.edges?.[eId]; if (!e) return null;
              const ab = st.halfEdges?.[e.halfEdgeA];
              const ba = st.halfEdges?.[e.halfEdgeB];
              if (!ab || !ba) return null;
              const nA = st.nodes?.[ab.originId];
              const nB = st.nodes?.[ba.originId];
              if (!nA || !nB) return null;
              const WS = 0.2;
              return (
                <line key={eId}
                  x1={nA.x*WS} y1={nA.y*WS} x2={nB.x*WS} y2={nB.y*WS}
                  stroke="#3b82f6" strokeWidth={2/zoom} opacity={0.55}/>
              );
            })}
          </g>
        )}

                {/* ── Crosshair (screen space) ───────────────────────────────────── */}
        {cursorScr && (
          <g style={{ pointerEvents: "none" }} opacity={0.25}>
            <line x1={0} y1={cursorScr.y} x2={svgSize.w} y2={cursorScr.y}
              stroke="#c4cdd8" strokeWidth={0.6}/>
            <line x1={cursorScr.x} y1={0} x2={cursorScr.x} y2={svgSize.h}
              stroke="#c4cdd8" strokeWidth={0.6}/>
            <circle cx={cursorScr.x} cy={cursorScr.y} r={3}
              fill="none" stroke="#e8a838" strokeWidth={1}/>
          </g>
        )}
      </svg>

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      <CADToolbar
        mode={mode} matId={matId} setMatId={setMatId}
        wallH={wallH} setWallH={setWallH}
        offsetDist={offsetDist} setOffsetDist={setOffsetDist}
        showTransformPanel={showTransformPanel}
        onToggleTransform={onToggleTransform}
        selCount={(selMany?.edges?.length || 0) + (selMany?.nodes?.length || 0)}
        onSetMode={onSetMode}
        onTemplates={onTemplates}
        onDrawing={onDrawing}
        onHeal={onHeal}
        onToggleLayers={onToggleLayers}
        showLayerPanel={showLayerPanelState}
        hasErr={hasErr} errCount={errCount}
        show3D={show3D} onToggle3D={onToggle3D}
        showAI={showAI} onToggleAI={onToggleAI}
        drawingMode={drawingMode} onSetDrawingMode={onSetDrawingMode}
      />

      {/* ── Transform panel ──────────────────────────────────────────────────── */}
      {showTransformPanel && (
        <TransformPanel
          selCount={(selMany?.edges?.length || 0) + (selMany?.nodes?.length || 0)}
          transformAngle={transformAngle}     setTransformAngle={setTransformAngle}
          transformScale={transformScale}     setTransformScale={setTransformScale}
          arrayRows={arrayRows}               setArrayRows={setArrayRows}
          arrayCols={arrayCols}               setArrayCols={setArrayCols}
          arrayRowSpacing={arrayRowSpacing}   setArrayRowSpacing={setArrayRowSpacing}
          arrayColSpacing={arrayColSpacing}   setArrayColSpacing={setArrayColSpacing}
          arrayCount={arrayCount}             setArrayCount={setArrayCount}
          arrayTotalAngle={arrayTotalAngle}   setArrayTotalAngle={setArrayTotalAngle}
          onCommand={onCommand}
          onClose={onToggleTransform}
        />
      )}

      {/* ── Mode hints ────────────────────────────────────────────────────────── */}
      {mode === "select"   && selMany && (selMany.edges.length + selMany.nodes.length) === 0 &&
        <HintBadge color="#6b7e94">SELECT — click object or drag to rubber-band · Ctrl+A all · Del to delete</HintBadge>}
      {mode === "wall"     && <HintBadge color="#3b82f6">WALL — click endpoints · dbl-click to end chain</HintBadge>}
      {mode === "room"     && <HintBadge color="#4ade80">ROOM — click inside a closed polygon</HintBadge>}
      {mode === "opening"  && <HintBadge color="#e8a838">OPENING — click on a wall segment</HintBadge>}
      {mode === "annotate" && <HintBadge color="#94a3b8">ANNOTATE — click to place text</HintBadge>}
      {mode === "dim"      && <HintBadge color="#818cf8">DIMENSION — click two points{wStart ? " · click second point" : ""}</HintBadge>}
      {mode === "offset"   && <HintBadge color="#22d3ee">OFFSET {offsetDist}mm — hover an edge · click to place parallel copy</HintBadge>}
      {mode === "mirror"   && <HintBadge color="#f472b6">MIRROR — {mirrorAxisStart ? "click 2nd axis point" : "click 1st axis point"}</HintBadge>}
      {mode === "adim"     && <HintBadge color="#818cf8">ANGULAR DIM — {adimPts?.length === 0 ? "click vertex" : adimPts?.length === 1 ? "click arm A" : "click arm B"}</HintBadge>}
      {mode === "trim"     && <HintBadge color="#fb923c">TRIM — {trimTarget ? "click cutting edge" : "click edge to trim"}</HintBadge>}
      {mode === "extend"   && <HintBadge color="#fb923c">EXTEND — {trimTarget ? "click boundary edge" : "click edge to extend"}</HintBadge>}
      {mode === "break"    && <HintBadge color="#fb923c">BREAK — click on an edge to split it at that point</HintBadge>}
      {mode === "circle"   && <HintBadge color="#22d3ee">CIRCLE — {!primDrawPts?.length ? "click center point" : "click radius point"}</HintBadge>}
      {mode === "arc"      && <HintBadge color="#22d3ee">ARC — {!primDrawPts?.length ? "click start point" : primDrawPts?.length === 1 ? "click point on arc" : "click end point"}</HintBadge>}
      {mode === "spline"   && <HintBadge color="#a78bfa">SPLINE — click points · double-click or Enter to finish ({primDrawPts?.length || 0} pts)</HintBadge>}
      {mode === "polyline" && <HintBadge color="#a78bfa">POLYLINE — click points · double-click or Enter to finish ({primDrawPts?.length || 0} pts)</HintBadge>}
      {activeSymbol        && <HintBadge color="#facc15">SYMBOL — click to place · Esc cancel</HintBadge>}

      {/* ── Inline annotation input ───────────────────────────────────────── */}
      {pendingAnnotation && (() => {
        const scr = w2s(pendingAnnotation, pan, zoom);
        return (
          <div style={{ position:"absolute", left:scr.x, top:Math.max(44, scr.y - 36), zIndex:50,
            background:"#030508", border:"1px solid #3b82f640", borderRadius:2,
            padding:"3px 6px", display:"flex", gap:4, alignItems:"center",
            boxShadow:"0 4px 12px #000a", minWidth:180 }}>
            <input autoFocus value={annotationDraft}
              onChange={e => setAnnotationDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && annotationDraft.trim()) {
                  commit(s => mutAddAnnotation(pendingAnnotation.x, pendingAnnotation.y,
                    annotationDraft.trim(), "callout", [], s));
                  setPendingAnnotation(null); setAnnotationDraft("");
                }
                if (e.key === "Escape") { setPendingAnnotation(null); setAnnotationDraft(""); }
              }}
              placeholder="annotation text"
              style={{ flex:1, background:"transparent", border:"none", color:"#dde6f0",
                fontSize:10, ...MONO, outline:"none" }}/>
            <button onClick={() => {
              if (annotationDraft.trim())
                commit(s => mutAddAnnotation(pendingAnnotation.x, pendingAnnotation.y,
                  annotationDraft.trim(), "callout", [], s));
              setPendingAnnotation(null); setAnnotationDraft("");
            }} style={{ background:"transparent", border:"1px solid #3b82f640", color:"#3b82f6",
              borderRadius:2, padding:"1px 7px", fontSize:9, cursor:"pointer", ...MONO }}>
              OK
            </button>
          </div>
        );
      })()}

      {/* ── Inline grid line input ────────────────────────────────────────── */}
      {pendingGridLine && (() => {
        const scr = w2s(pendingGridLine, pan, zoom);
        return (
          <div style={{ position:"absolute", left:scr.x, top:Math.max(44, scr.y - 36), zIndex:50,
            background:"#030508", border:"1px solid #f9731640", borderRadius:2,
            padding:"3px 6px", display:"flex", gap:4, alignItems:"center",
            boxShadow:"0 4px 12px #000a", minWidth:150 }}>
            <input autoFocus value={annotationDraft}
              onChange={e => setAnnotationDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  commit(s => mutAddGridLine("x", pendingGridLine.x, annotationDraft.trim() || "?", s));
                  setPendingGridLine(null); setAnnotationDraft("");
                }
                if (e.key === "Escape") { setPendingGridLine(null); setAnnotationDraft(""); }
              }}
              placeholder="grid label (A, 1…)"
              style={{ flex:1, background:"transparent", border:"none", color:"#f97316",
                fontSize:10, ...MONO, outline:"none" }}/>
            <button onClick={() => {
              commit(s => mutAddGridLine("x", pendingGridLine.x, annotationDraft.trim() || "?", s));
              setPendingGridLine(null); setAnnotationDraft("");
            }} style={{ background:"transparent", border:"1px solid #f9731640", color:"#f97316",
              borderRadius:2, padding:"1px 7px", fontSize:9, cursor:"pointer", ...MONO }}>
              OK
            </button>
          </div>
        );
      })()}

      {/* ── Status bar — thin, technical ─────────────────────────────────── */}
      <div style={{
        position:"absolute", bottom:0, left:0, right:0, height:20,
        background:"#030508", borderTop:"1px solid #0a0f18",
        display:"flex", alignItems:"center", padding:"0 10px",
        gap:16, fontSize:9, color:"#1e2a36", ...MONO,
      }}>
        {/* Coordinates */}
        <span>
          X <span style={{ color:"#22c55e" }}>{snap?.point ? Math.round(snap.point.x) : 0}</span>
          {"  "}Y <span style={{ color:"#22c55e" }}>{snap?.point ? Math.round(snap.point.y) : 0}</span>
          <span style={{ color:"#131924" }}> mm</span>
        </span>

        {/* Snap type */}
        {snap && snap.type !== "grid" && (
          <span style={{
            color:{ node:"#22d3ee", intersection:"#e8a838", edge:"#818cf8", midpoint:"#4ade80" }[snap.type] || "#fff",
          }}>
            {snap.type.toUpperCase()}
          </span>
        )}

        {/* Mode */}
        <span style={{ color:"#3b82f6" }}>{mode.toUpperCase()}</span>

        {/* Zoom */}
        <span>{Math.round(zoom * 100)}%</span>

        {/* Chain indicator */}
        {wStart && (
          <span style={{ color:"#e8a838" }}>⬤ CHAIN</span>
        )}
        {dragNode && (
          <span style={{ color:"#e8a838" }}>⬤ DRAG</span>
        )}
        {selMany && (selMany.edges.length + selMany.nodes.length + selMany.symbols.length + selMany.annotations.length) > 0 && !dragNodes && (
          <span style={{ color:"#3b82f6" }}>
            ⬤ {selMany.edges.length + selMany.nodes.length + selMany.symbols.length + selMany.annotations.length} SELECTED
          </span>
        )}
        {dragNodes && dragDelta && (
          <span style={{ color:"#e8a838" }}>
            ⬤ MOVING {dragNodes.length} NODE{dragNodes.length !== 1 ? "S" : ""}
            {"  "}Δ({Math.round(dragDelta.dx)}, {Math.round(dragDelta.dy)}) mm
          </span>
        )}

        {/* Object counts */}
        <span style={{ marginLeft:"auto", color:"#0d1520" }}>
          {Object.keys(st.edges||{}).length}W
          {"  "}{Object.keys(st.faces||{}).length}R
          {"  "}{Object.keys(st.openings||{}).length}O
          {"  "}{Object.keys(st.symbols||{}).length}S
          {"  "}Alt+drag pan · scroll zoom
        </span>
      </div>

      {/* ── Overlays ─────────────────────────────────────────────────────── */}
      <DimEditor
        editing={editingDim} zoom={zoom} pan={pan}
        onCommit={n => { if (editingDim) { commit(s => mutResizeWall(editingDim.eId, n, s)); setEditingDim(null); } }}
        onCancel={() => setEditingDim(null)}
      />
      {roomPopup && (
        <RoomPopup faceId={roomPopup.faceId} st={st} screenPt={roomPopup.screenPt}
          onAssign={(rt, rn) => { commit(s => mutSetRoom(roomPopup.faceId, rt, rn, s)); setRoomPopup(null); clearSel(); }}
          onClose={() => { setRoomPopup(null); clearSel(); }}/>
      )}
      {openingPopup && (
        <OpeningPopup edgeId={openingPopup.edgeId} clickT={openingPopup.t} screenPt={openingPopup.screenPt}
          onAdd={(preset, t) => { commit(s => mutAddOpening(openingPopup.edgeId, preset, t, s)); setOpeningPopup(null); clearSel(); }}
          onClose={() => { setOpeningPopup(null); clearSel(); }}/>
      )}
    </div>
  );
}
